import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Typography,
  Upload,
  message,
  Select,
  Tag,
  Popconfirm,
  Empty,
  Spin,
  Alert,
  Breadcrumb,
  Tabs
} from 'antd';
import {
  FolderOutlined,
  FileOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  ShareAltOutlined,
  FolderAddOutlined,
  FileAddOutlined,
  HomeOutlined,
  UserOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface Folder {
  id: string;
  name: string;
  description?: string;
  parent_id?: string;
  created_by?: string;
  creator_first_name?: string;
  creator_last_name?: string;
  is_shared_with_all_sales: boolean;
  is_active: boolean;
  document_count: number;
  subfolder_count: number;
  can_upload?: boolean;
  can_download?: boolean;
  can_delete?: boolean;
  created_at: string;
}

interface Document {
  id: string;
  name: string;
  description?: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  folder_id?: string;
  folder_name?: string;
  uploaded_by?: string;
  uploader_first_name?: string;
  uploader_last_name?: string;
  is_shared_with_all_sales: boolean;
  is_active: boolean;
  can_download?: boolean;
  can_delete?: boolean;
  created_at: string;
}

interface SalesAgent {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

const SalesPortalAdmin: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [folderStack, setFolderStack] = useState<Folder[]>([]);
  const [activeTab, setActiveTab] = useState<'agent' | 'all-agents'>('agent');
  const [folderModalVisible, setFolderModalVisible] = useState(false);
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);
  const [permissionsType, setPermissionsType] = useState<'folder' | 'document' | null>(null);
  const [permissionsId, setPermissionsId] = useState<string | null>(null);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [folderForm] = Form.useForm();
  const [documentForm] = Form.useForm();
  const [permissionsForm] = Form.useForm();

  // Fetch all folders for admin management
  const { data: allFoldersData } = useQuery(
    ['sales-portal-all-folders', activeTab === 'all-agents' ? selectedFolderId : null],
    async () => {
      const params = new URLSearchParams();
      if (activeTab === 'all-agents' && selectedFolderId) {
        params.append('parent_id', selectedFolderId);
      } else if (activeTab === 'all-agents') {
        params.append('parent_id', 'null');
      }
      const response = await api.get(`/api/sales-portal/admin/folders?${params.toString()}`);
      return response.data;
    }
  );

  // Fetch all documents for admin management
  const { data: allDocumentsData } = useQuery(
    ['sales-portal-all-documents', activeTab === 'all-agents' ? selectedFolderId : null],
    async () => {
      const params = new URLSearchParams();
      if (activeTab === 'all-agents') {
        if (selectedFolderId) {
          params.append('folder_id', selectedFolderId);
        } else {
          params.append('folder_id', 'null');
        }
      }
      const response = await api.get(`/api/sales-portal/admin/documents?${params.toString()}`);
      return response.data;
    }
  );

  // Fetch agent view (what the selected agent can see)
  const { data: agentViewData, isLoading: agentViewLoading } = useQuery(
    ['sales-portal-agent-view', selectedAgentId, selectedFolderId],
    async () => {
      if (!selectedAgentId) return null;
      const params = new URLSearchParams();
      if (selectedFolderId) {
        params.append('folder_id', selectedFolderId);
      } else {
        params.append('folder_id', 'null');
      }
      const response = await api.get(`/api/sales-portal/admin/agent-view/${selectedAgentId}?${params.toString()}`);
      return response.data;
    },
    { enabled: !!selectedAgentId }
  );

  // Fetch sales agents
  const { data: agentsData } = useQuery(
    ['sales-portal-agents'],
    async () => {
      const response = await api.get('/api/sales-portal/admin/sales-agents');
      return response.data;
    }
  );

  // Fetch folder permissions
  const { data: folderPermissionsData } = useQuery(
    ['sales-portal-folder-permissions', permissionsId],
    async () => {
      if (!permissionsId || permissionsType !== 'folder') return null;
      const response = await api.get(`/api/sales-portal/admin/folders/${permissionsId}/permissions`);
      return response.data;
    },
    { enabled: permissionsModalVisible && permissionsType === 'folder' && !!permissionsId }
  );

  // Fetch document permissions
  const { data: documentPermissionsData } = useQuery(
    ['sales-portal-document-permissions', permissionsId],
    async () => {
      if (!permissionsId || permissionsType !== 'document') return null;
      const response = await api.get(`/api/sales-portal/admin/documents/${permissionsId}/permissions`);
      return response.data;
    },
    { enabled: permissionsModalVisible && permissionsType === 'document' && !!permissionsId }
  );

  const allFolders: Folder[] = allFoldersData?.folders || [];
  const allDocuments: Document[] = allDocumentsData?.documents || [];
  const agentFolders: Folder[] = agentViewData?.folders || [];
  const agentDocuments: Document[] = agentViewData?.documents || [];
  const agents: SalesAgent[] = useMemo(() => agentsData?.agents ?? [], [agentsData]);

  // Check if the current item is shared with all agents
  const getCurrentItem = () => {
    if (!permissionsId || !permissionsType) return null;
    if (permissionsType === 'folder') {
      return allFolders.find(f => f.id === permissionsId) || agentSpecificFolders.find(f => f.id === permissionsId);
    } else {
      return allAgentsDocuments.find(d => d.id === permissionsId) || agentSpecificDocuments.find(d => d.id === permissionsId);
    }
  };

  const currentItem = getCurrentItem();
  const isSharedWithAll = currentItem?.is_shared_with_all_sales === true;

  // Load permissions into form when they're fetched
  useEffect(() => {
    if (!permissionsModalVisible || !permissionsId || !permissionsType) return;

    const permissionsData = permissionsType === 'folder' ? folderPermissionsData : documentPermissionsData;
    
    if (permissionsData?.permissions) {
      // Extract agent IDs from permissions
      const agentIds = permissionsData.permissions.map((p: any) => p.sales_agent_id);
      
      // If item is shared with all, select all agents
      const selectedAgentIds = isSharedWithAll ? agents.map(a => a.id) : agentIds;
      
      // Get permission values (use first permission as default, or set defaults)
      const firstPermission = permissionsData.permissions[0];
      const formValues: any = {
        sales_agent_ids: selectedAgentIds,
        can_download: firstPermission?.can_download !== false,
        can_delete: firstPermission?.can_delete === true
      };
      
      // Only add can_upload for folders
      if (permissionsType === 'folder') {
        formValues.can_upload = firstPermission?.can_upload !== false;
      }
      
      permissionsForm.setFieldsValue(formValues);
      } else {
        // Set defaults
        const defaultValues: any = {
          sales_agent_ids: [],
          can_download: true,
          can_delete: false
        };
        
        // Only add can_upload for folders
        if (permissionsType === 'folder') {
          defaultValues.can_upload = true;
        }
        
        if (isSharedWithAll) {
          // For items shared with all, select all agents by default
          defaultValues.sales_agent_ids = agents.map(a => a.id);
        } else if (selectedAgentId) {
          // For agent-specific items, use selected agent
          defaultValues.sales_agent_ids = [selectedAgentId];
        }
        
        permissionsForm.setFieldsValue(defaultValues);
      }
  }, [permissionsModalVisible, permissionsId, permissionsType, folderPermissionsData, documentPermissionsData, isSharedWithAll, selectedAgentId, agents, permissionsForm]);

  // Create folder mutation
  const createFolderMutation = useMutation(
    async (values: any) => {
      const response = await api.post('/api/sales-portal/admin/folders', values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Folder created successfully');
        queryClient.invalidateQueries(['sales-portal-all-folders']);
        queryClient.invalidateQueries(['sales-portal-agent-view']);
        queryClient.invalidateQueries(['sales-portal-all-documents']);
        // Force refetch if agent is selected
        if (selectedAgentId) {
          queryClient.refetchQueries(['sales-portal-agent-view', selectedAgentId, selectedFolderId]);
        }
        setFolderModalVisible(false);
        folderForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create folder');
      }
    }
  );

  // Update folder mutation
  const updateFolderMutation = useMutation(
    async ({ id, values }: { id: string; values: any }) => {
      const response = await api.put(`/api/sales-portal/admin/folders/${id}`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Folder updated successfully');
        queryClient.invalidateQueries(['sales-portal-all-folders']);
        queryClient.invalidateQueries(['sales-portal-agent-view']);
        setFolderModalVisible(false);
        setEditingFolder(null);
        folderForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update folder');
      }
    }
  );

  // Delete folder mutation
  const deleteFolderMutation = useMutation(
    async (id: string) => {
      await api.delete(`/api/sales-portal/admin/folders/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Folder deleted successfully');
        queryClient.invalidateQueries(['sales-portal-all-folders']);
        queryClient.invalidateQueries(['sales-portal-agent-view']);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete folder');
      }
    }
  );

  // Upload document mutation
  const uploadDocumentMutation = useMutation(
    async (formData: FormData) => {
      const response = await api.post('/api/sales-portal/admin/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Document uploaded successfully');
        queryClient.invalidateQueries(['sales-portal-all-documents']);
        queryClient.invalidateQueries(['sales-portal-agent-view']);
        // Force refetch if agent is selected
        if (selectedAgentId) {
          queryClient.refetchQueries(['sales-portal-agent-view', selectedAgentId, selectedFolderId]);
        }
        setDocumentModalVisible(false);
        documentForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to upload document');
      }
    }
  );

  // Update document mutation
  const updateDocumentMutation = useMutation(
    async ({ id, values }: { id: string; values: any }) => {
      const response = await api.put(`/api/sales-portal/admin/documents/${id}`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Document updated successfully');
        queryClient.invalidateQueries(['sales-portal-all-documents']);
        queryClient.invalidateQueries(['sales-portal-agent-view']);
        setDocumentModalVisible(false);
        setEditingDocument(null);
        documentForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update document');
      }
    }
  );

  // Delete document mutation
  const deleteDocumentMutation = useMutation(
    async (id: string) => {
      await api.delete(`/api/sales-portal/admin/documents/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Document deleted successfully');
        queryClient.invalidateQueries(['sales-portal-all-documents']);
        queryClient.invalidateQueries(['sales-portal-agent-view']);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete document');
      }
    }
  );

  // Assign permissions mutation
  const assignPermissionsMutation = useMutation(
    async ({ type, id, sales_agent_ids, permissions }: { type: 'folder' | 'document'; id: string; sales_agent_ids: string[]; permissions: any }) => {
      const endpoint = type === 'folder' 
        ? `/api/sales-portal/admin/folders/${id}/permissions`
        : `/api/sales-portal/admin/documents/${id}/permissions`;
      const response = await api.post(endpoint, { sales_agent_ids, permissions });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Permissions updated successfully');
        queryClient.invalidateQueries(['sales-portal-folder-permissions']);
        queryClient.invalidateQueries(['sales-portal-document-permissions']);
        queryClient.invalidateQueries(['sales-portal-agent-view']);
        // Force refetch if agent is selected
        if (selectedAgentId) {
          queryClient.refetchQueries(['sales-portal-agent-view', selectedAgentId, selectedFolderId]);
        }
        setPermissionsModalVisible(false);
        permissionsForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update permissions');
      }
    }
  );

  const handleAgentChange = (agentId: string | null) => {
    setSelectedAgentId(agentId);
    setSelectedFolderId(null);
    setFolderStack([]);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key as 'agent' | 'all-agents');
    // Reset folder navigation when switching tabs
    setSelectedFolderId(null);
    setFolderStack([]);
  };

  const handleFolderClick = (folder: Folder) => {
    setFolderStack([...folderStack, folder]);
    setSelectedFolderId(folder.id);
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setSelectedFolderId(null);
      setFolderStack([]);
    } else {
      const newStack = folderStack.slice(0, index + 1);
      setFolderStack(newStack);
      setSelectedFolderId(newStack[newStack.length - 1].id);
    }
  };

  const handleCreateFolder = (forAllAgents: boolean = false) => {
    setEditingFolder(null);
    folderForm.resetFields();
    folderForm.setFieldsValue({ 
      // parent_id will be set automatically based on selectedFolderId when submitting
      is_shared_with_all_sales: forAllAgents // Set based on which tab is creating
    });
    setFolderModalVisible(true);
  };

  const handleEditFolder = (folder: Folder) => {
    setEditingFolder(folder);
    folderForm.setFieldsValue({
      name: folder.name,
      description: folder.description,
      // Don't show parent_id in edit - folders stay where they are
      is_shared_with_all_sales: folder.is_shared_with_all_sales
    });
    setFolderModalVisible(true);
  };

  const handleFolderSubmit = (values: any) => {
    if (editingFolder) {
      // When editing, don't change parent_id (folders stay where they are)
      const { parent_id, ...updateValues } = values;
      updateFolderMutation.mutate({ id: editingFolder.id, values: updateValues });
    } else {
      // When creating, automatically create in current location
      // If "share with all" is false and an agent is selected, include agent ID to auto-assign permissions
      createFolderMutation.mutate({
        ...values,
        parent_id: selectedFolderId || null,
        // Include sales_agent_id if not sharing with all and an agent is selected
        ...(values.is_shared_with_all_sales === false && selectedAgentId ? { sales_agent_id: selectedAgentId } : {})
      });
    }
  };

  const handleUploadDocument = (forAllAgents: boolean = false) => {
    setEditingDocument(null);
    documentForm.resetFields();
    documentForm.setFieldsValue({ 
      folder_id: selectedFolderId || null,
      is_shared_with_all_sales: forAllAgents // Set based on which tab is creating
    });
    setDocumentModalVisible(true);
  };

  const handleEditDocument = (document: Document) => {
    setEditingDocument(document);
    documentForm.setFieldsValue({
      name: document.name,
      description: document.description,
      folder_id: document.folder_id || null,
      is_shared_with_all_sales: document.is_shared_with_all_sales
    });
    setDocumentModalVisible(true);
  };

  const handleDocumentSubmit = async (values: any) => {
    if (editingDocument) {
      updateDocumentMutation.mutate({ id: editingDocument.id, values });
    } else {
      const formData = new FormData();
      formData.append('name', values.name);
      if (values.description) formData.append('description', values.description);
      if (values.folder_id) formData.append('folder_id', values.folder_id);
      // Convert boolean to string for FormData
      formData.append('is_shared_with_all_sales', values.is_shared_with_all_sales === true ? 'true' : 'false');
      
      const fileList = documentForm.getFieldValue('file')?.fileList;
      if (fileList && fileList.length > 0) {
        formData.append('file', fileList[0].originFileObj);
      } else if (!editingDocument) {
        message.error('Please select a file to upload');
        return;
      }

      uploadDocumentMutation.mutate(formData);
    }
  };

  const handleManagePermissions = (type: 'folder' | 'document', id: string) => {
    setPermissionsType(type);
    setPermissionsId(id);
    setPermissionsModalVisible(true);
    
    // Reset form - permissions will be loaded by the query and set in useEffect
    permissionsForm.resetFields();
  };

  const handlePermissionsSubmit = (values: any) => {
    if (!permissionsId || !permissionsType) return;

    // If item is shared with all agents, use all agent IDs
    // Otherwise use the selected agent IDs from the form
    const effectiveAgentIds = isSharedWithAll 
      ? agents.map(a => a.id) 
      : (values.sales_agent_ids || []);

    if (effectiveAgentIds.length === 0 && !isSharedWithAll) {
      message.error('Please select at least one sales agent');
      return;
    }

    assignPermissionsMutation.mutate({
      type: permissionsType,
      id: permissionsId,
      sales_agent_ids: effectiveAgentIds,
      permissions: {
        can_upload: values.can_upload !== false,
        can_download: values.can_download !== false,
        can_delete: values.can_delete === true
      }
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileType = (fileType: string | undefined) => {
    if (!fileType) return '-';
    // Extract extension from MIME type (e.g., "application/pdf" -> "PDF")
    const parts = fileType.split('/');
    if (parts.length > 1) {
      return parts[1].toUpperCase();
    }
    return fileType.toUpperCase();
  };

  const selectedAgent = agents.find(a => a.id === selectedAgentId);

  // Filter folders and documents for "All Agents View" (shared with all)
  const allAgentsFolders = (allFolders || []).filter(f => f.is_shared_with_all_sales === true);
  const allAgentsDocuments = (allDocuments || []).filter(d => d.is_shared_with_all_sales === true);

  // For "Agent View", show only agent-specific items (not shared with all)
  const agentSpecificFolders = (agentFolders || []).filter(f => f.is_shared_with_all_sales !== true);
  const agentSpecificDocuments = (agentDocuments || []).filter(d => d.is_shared_with_all_sales !== true);

  return (
    <div>
      <Card
        title={
          <Space>
            <FolderOutlined />
            <span>Sales Agent Portal Management</span>
          </Space>
        }
      >
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          {/* Agent View Tab */}
          <TabPane tab="Agent View" key="agent">
            <Card
              size="small"
              extra={
                <Space>
                  <Button 
                    icon={<FolderAddOutlined />} 
                    onClick={() => handleCreateFolder(false)}
                    disabled={!selectedAgentId}
                  >
                    New Folder
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<FileAddOutlined />} 
                    onClick={() => handleUploadDocument(false)}
                    disabled={!selectedAgentId}
                  >
                    Upload Document
                  </Button>
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* Sales Agent Selector */}
                <Card size="small">
                  <Space>
                    <UserOutlined />
                    <Text strong>Select Sales Agent:</Text>
                    <Select
                      placeholder="Select a sales agent to view their access"
                      style={{ width: 400 }}
                      value={selectedAgentId}
                      onChange={handleAgentChange}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {agents.map(agent => (
                        <Option key={agent.id} value={agent.id}>
                          {agent.first_name} {agent.last_name} ({agent.email})
                        </Option>
                      ))}
                    </Select>
                  </Space>
                </Card>

          {selectedAgentId && (
            <>
              <Alert
                message={`Viewing access for: ${selectedAgent?.first_name} ${selectedAgent?.last_name}`}
                description="This shows what folders and documents this sales agent can access. Click on a folder to see its contents."
                type="info"
                showIcon
              />

              {/* Breadcrumb */}
              <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
                <Breadcrumb
                  separator={<span style={{ color: '#d9d9d9', margin: '0 8px' }}>/</span>}
                  style={{ fontSize: '14px' }}
                >
                  <Breadcrumb.Item>
                    <button
                      type="button"
                      onClick={() => handleBreadcrumbClick(-1)}
                      style={{ 
                        cursor: 'pointer',
                        color: folderStack.length === 0 ? '#1890ff' : '#595959',
                        fontWeight: folderStack.length === 0 ? 500 : 400,
                        textDecoration: 'none',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#1890ff'}
                      onMouseLeave={(e) => e.currentTarget.style.color = folderStack.length === 0 ? '#1890ff' : '#595959'}
                    >
                      <HomeOutlined style={{ marginRight: 4 }} />
                      Root
                    </button>
                  </Breadcrumb.Item>
                  {folderStack.map((folder, index) => (
                    <Breadcrumb.Item key={folder.id}>
                      <button
                        type="button"
                        onClick={() => handleBreadcrumbClick(index)}
                        style={{ 
                          cursor: 'pointer',
                          color: index === folderStack.length - 1 ? '#1890ff' : '#595959',
                          fontWeight: index === folderStack.length - 1 ? 500 : 400,
                          textDecoration: 'none',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                        }}
                        onMouseEnter={(e) => {
                          if (index !== folderStack.length - 1) {
                            e.currentTarget.style.color = '#1890ff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (index !== folderStack.length - 1) {
                            e.currentTarget.style.color = '#595959';
                          }
                        }}
                      >
                        {index === folderStack.length - 1 && <FolderOutlined style={{ marginRight: 4 }} />}
                        {folder.name}
                      </button>
                    </Breadcrumb.Item>
                  ))}
                </Breadcrumb>
              </Card>

              {/* Agent View - Combined Folders and Documents */}
              <Card title="Agent's View" size="small">
                {agentViewLoading ? (
                  <Spin />
                ) : agentSpecificFolders.length === 0 && agentSpecificDocuments.length === 0 ? (
                  <Empty description="No folders or documents accessible" />
                ) : (
                  <Table
                    dataSource={[
                      ...agentSpecificFolders.map(f => ({ ...f, type: 'folder' })),
                      ...agentSpecificDocuments.map(d => ({ ...d, type: 'document' }))
                    ]}
                    rowKey={(record) => `${record.type}-${record.id}`}
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: 'Type',
                        key: 'type',
                        width: 80,
                        render: (_, record) => (
                          record.type === 'folder' ? (
                            <FolderOutlined style={{ fontSize: 20 }} />
                          ) : (
                            <FileOutlined style={{ fontSize: 20 }} />
                          )
                        )
                      },
                       {
                         title: 'Name',
                         key: 'name',
                         render: (_, record) => (
                           <Space>
                             {record.type === 'folder' ? (
                               <button
                                 type="button"
                                 onClick={() => handleFolderClick(record as Folder)}
                                 style={{ cursor: 'pointer', color: '#1890ff', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                               >
                                 {record.name}
                               </button>
                             ) : (
                               <span>{record.name}</span>
                             )}
                             {record.is_shared_with_all_sales && (
                               <Tag color="green">All Sales</Tag>
                             )}
                             {record.type === 'folder' && (record as Folder).can_upload && !selectedAgentId && (
                               <Tag color="blue">Can Upload</Tag>
                             )}
                           </Space>
                         )
                       },
                      {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        render: (text) => text || '-'
                      },
                      {
                        title: 'Size',
                        key: 'size',
                        render: (_, record) => 
                          record.type === 'document' ? formatFileSize((record as Document).file_size) : '-'
                      },
                      {
                        title: 'File Type',
                        key: 'file_type',
                        width: 120,
                        render: (_, record) => 
                          record.type === 'document' ? getFileType((record as Document).file_type) : '-'
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        width: 200,
                        render: (_, record) => (
                          <Space>
                            {record.type === 'folder' && (
                              <>
                                <Button
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEditFolder(record as Folder)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => handleManagePermissions('folder', record.id)}
                                >
                                  Permissions
                                </Button>
                              </>
                            )}
                            {record.type === 'document' && (
                              <>
                                <Button
                                  size="small"
                                  icon={<EyeOutlined />}
                                  onClick={async () => {
                                    try {
                                      const response = await api.get(`/api/sales-portal/admin/documents/${record.id}/download`, {
                                        responseType: 'blob'
                                      });
                                      const blob = new Blob([response.data], { type: (record as Document).file_type || 'application/octet-stream' });
                                      const url = window.URL.createObjectURL(blob);
                                      const win = window.open(url, '_blank');
                                      if (!win) {
                                        const a = window.document.createElement('a');
                                        a.href = url;
                                        a.target = '_blank';
                                        window.document.body.appendChild(a);
                                        a.click();
                                        window.document.body.removeChild(a);
                                      }
                                      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                                    } catch (error: any) {
                                      message.error(error.response?.data?.error || 'Failed to preview document');
                                    }
                                  }}
                                >
                                  Preview
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => handleManagePermissions('document', record.id)}
                                >
                                  Permissions
                                </Button>
                              </>
                            )}
                            <Popconfirm
                              title={`Delete ${record.type}?`}
                              description={record.type === 'folder' ? 'This will also delete all subfolders and documents inside.' : ''}
                              onConfirm={() => {
                                if (record.type === 'folder') {
                                  deleteFolderMutation.mutate(record.id);
                                } else {
                                  deleteDocumentMutation.mutate(record.id);
                                }
                              }}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                              />
                            </Popconfirm>
                          </Space>
                        )
                      }
                    ]}
                  />
                )}
              </Card>
            </>
          )}

          {!selectedAgentId && (
            <Alert
              message="Select a Sales Agent"
              description="Please select a sales agent from the dropdown above to view and manage their folder and document access."
              type="info"
              showIcon
            />
          )}
              </Space>
            </Card>
          </TabPane>

          {/* All Agents View Tab */}
          <TabPane tab="All Agents View" key="all-agents">
            <Card
              size="small"
              extra={
                <Space>
                  <Button 
                    icon={<FolderAddOutlined />} 
                    onClick={() => handleCreateFolder(true)}
                  >
                    New Folder
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<FileAddOutlined />} 
                    onClick={() => handleUploadDocument(true)}
                  >
                    Upload Document
                  </Button>
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Alert
                  message="All Agents View"
                  description="This shows all folders and documents that are shared with all sales agents. These items are visible to every sales agent. Click on a folder to see its contents."
                  type="info"
                  showIcon
                />

                {/* Breadcrumb */}
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
                  <Breadcrumb
                    separator={<span style={{ color: '#d9d9d9', margin: '0 8px' }}>/</span>}
                    style={{ fontSize: '14px' }}
                  >
                    <Breadcrumb.Item>
                      <button
                        type="button"
                        onClick={() => handleBreadcrumbClick(-1)}
                        style={{ 
                          cursor: 'pointer',
                          color: folderStack.length === 0 ? '#1890ff' : '#595959',
                          fontWeight: folderStack.length === 0 ? 500 : 400,
                          textDecoration: 'none',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#1890ff'}
                        onMouseLeave={(e) => e.currentTarget.style.color = folderStack.length === 0 ? '#1890ff' : '#595959'}
                      >
                        <HomeOutlined style={{ marginRight: 4 }} />
                        Root
                      </button>
                    </Breadcrumb.Item>
                    {folderStack.map((folder, index) => (
                      <Breadcrumb.Item key={folder.id}>
                        <button
                          type="button"
                          onClick={() => handleBreadcrumbClick(index)}
                          style={{ 
                            cursor: 'pointer',
                            color: index === folderStack.length - 1 ? '#1890ff' : '#595959',
                            fontWeight: index === folderStack.length - 1 ? 500 : 400,
                            textDecoration: 'none',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                          }}
                          onMouseEnter={(e) => {
                            if (index !== folderStack.length - 1) {
                              e.currentTarget.style.color = '#1890ff';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (index !== folderStack.length - 1) {
                              e.currentTarget.style.color = '#595959';
                            }
                          }}
                        >
                          {index === folderStack.length - 1 && <FolderOutlined style={{ marginRight: 4 }} />}
                          {folder.name}
                        </button>
                      </Breadcrumb.Item>
                    ))}
                  </Breadcrumb>
                </Card>

                {/* All Agents View - Folders and Documents */}
                <Card title={selectedFolderId ? `Contents of: ${folderStack[folderStack.length - 1]?.name || 'Folder'}` : "Shared with All Sales Agents"} size="small">
                  {allAgentsFolders.length === 0 && allAgentsDocuments.length === 0 ? (
                    <Empty description="No folders or documents shared with all agents" />
                  ) : (
                    <Table
                      dataSource={[
                        ...allAgentsFolders.map(f => ({ ...f, type: 'folder' })),
                        ...allAgentsDocuments.map(d => ({ ...d, type: 'document' }))
                      ]}
                      rowKey={(record) => `${record.type}-${record.id}`}
                      size="small"
                      pagination={false}
                      columns={[
                        {
                          title: 'Type',
                          key: 'type',
                          width: 80,
                          render: (_, record) => (
                            record.type === 'folder' ? (
                              <FolderOutlined style={{ fontSize: 20 }} />
                            ) : (
                              <FileOutlined style={{ fontSize: 20 }} />
                            )
                          )
                        },
                        {
                          title: 'Name',
                          key: 'name',
                          render: (_, record) => (
                            <Space>
                              {record.type === 'folder' ? (
                                <button
                                  type="button"
                                  onClick={() => handleFolderClick(record as Folder)}
                                  style={{ cursor: 'pointer', color: '#1890ff', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                                >
                                  {record.name}
                                </button>
                              ) : (
                                <span>{record.name}</span>
                              )}
                              <Tag color="green">All Sales</Tag>
                            </Space>
                          )
                        },
                        {
                          title: 'Description',
                          dataIndex: 'description',
                          key: 'description',
                          render: (text) => text || '-'
                        },
                        {
                          title: 'Size',
                          key: 'size',
                          render: (_, record) => 
                            record.type === 'document' ? formatFileSize((record as Document).file_size) : '-'
                        },
                        {
                          title: 'File Type',
                          key: 'file_type',
                          width: 120,
                          render: (_, record) => 
                            record.type === 'document' ? getFileType((record as Document).file_type) : '-'
                        },
                        {
                          title: 'Actions',
                          key: 'actions',
                          width: 200,
                          render: (_, record) => (
                            <Space>
                              {record.type === 'folder' && (
                                <>
                                  <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditFolder(record as Folder)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => handleManagePermissions('folder', record.id)}
                                  >
                                    Permissions
                                  </Button>
                                </>
                              )}
                              {record.type === 'document' && (
                                <>
                                  <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={async () => {
                                      try {
                                        const response = await api.get(`/api/sales-portal/admin/documents/${record.id}/download`, {
                                          responseType: 'blob'
                                        });
                                        const blob = new Blob([response.data], { type: (record as Document).file_type || 'application/octet-stream' });
                                        const url = window.URL.createObjectURL(blob);
                                        const win = window.open(url, '_blank');
                                        if (!win) {
                                          const a = window.document.createElement('a');
                                          a.href = url;
                                          a.target = '_blank';
                                          window.document.body.appendChild(a);
                                          a.click();
                                          window.document.body.removeChild(a);
                                        }
                                        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                                      } catch (error: any) {
                                        message.error(error.response?.data?.error || 'Failed to preview document');
                                      }
                                    }}
                                  >
                                    Preview
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditDocument(record as Document)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => handleManagePermissions('document', record.id)}
                                  >
                                    Permissions
                                  </Button>
                                </>
                              )}
                              <Popconfirm
                                title={`Delete ${record.type}?`}
                                description={record.type === 'folder' ? 'This will also delete all subfolders and documents inside.' : ''}
                                onConfirm={() => {
                                  if (record.type === 'folder') {
                                    deleteFolderMutation.mutate(record.id);
                                  } else {
                                    deleteDocumentMutation.mutate(record.id);
                                  }
                                }}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                />
                              </Popconfirm>
                            </Space>
                          )
                        }
                      ]}
                    />
                  )}
                </Card>
              </Space>
            </Card>
          </TabPane>
        </Tabs>
      </Card>

      {/* Folder Modal */}
      <Modal
        title={editingFolder ? 'Edit Folder' : 'Create Folder'}
        open={folderModalVisible}
        onCancel={() => {
          setFolderModalVisible(false);
          setEditingFolder(null);
          folderForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={folderForm}
          layout="vertical"
          onFinish={handleFolderSubmit}
        >
          <Form.Item
            name="name"
            label="Folder Name"
            rules={[{ required: true, message: 'Folder name is required' }]}
          >
            <Input placeholder="Enter folder name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter folder description" />
          </Form.Item>
          {!editingFolder && (
            <Alert
              message={`Folder will be created in: ${selectedFolderId ? (folderStack[folderStack.length - 1]?.name || 'Current Location') : 'Root'}`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Form.Item
            name="is_shared_with_all_sales"
            valuePropName="checked"
            label="Share with all sales agents"
            tooltip={editingFolder ? "If checked, this folder will be visible to all sales agents. If unchecked, you'll need to assign specific permissions." : activeTab === 'all-agents' ? "This folder will be shared with all sales agents." : "If checked, this folder will be visible to all sales agents. If unchecked, you'll need to assign specific permissions."}
          >
            <Switch 
              checkedChildren="All Sales" 
              unCheckedChildren="Specific Only"
              disabled={!editingFolder && activeTab === 'all-agents'}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createFolderMutation.isLoading || updateFolderMutation.isLoading}>
                {editingFolder ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => {
                setFolderModalVisible(false);
                setEditingFolder(null);
                folderForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Document Modal */}
      <Modal
        title={editingDocument ? 'Edit Document' : 'Upload Document'}
        open={documentModalVisible}
        onCancel={() => {
          setDocumentModalVisible(false);
          setEditingDocument(null);
          documentForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={documentForm}
          layout="vertical"
          onFinish={handleDocumentSubmit}
        >
          {!editingDocument && (
            <Form.Item
              name="file"
              label="File"
              rules={[{ required: true, message: 'Please select a file' }]}
            >
              <Upload
                maxCount={1}
                beforeUpload={() => false}
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
            </Form.Item>
          )}
          <Form.Item
            name="name"
            label="Document Name"
            rules={[{ required: true, message: 'Document name is required' }]}
          >
            <Input placeholder="Enter document name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter document description" />
          </Form.Item>
          <Form.Item
            name="folder_id"
            label="Folder"
          >
            <Select
              placeholder="Select folder (leave empty for root)"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {allFolders.map(folder => (
                <Option key={folder.id} value={folder.id}>
                  {folder.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="is_shared_with_all_sales"
            valuePropName="checked"
            label="Share with all sales agents"
            tooltip={editingDocument ? "If checked, this document will be visible to all sales agents. If unchecked, you'll need to assign specific permissions." : activeTab === 'all-agents' ? "This document will be shared with all sales agents." : "If checked, this document will be visible to all sales agents. If unchecked, you'll need to assign specific permissions."}
          >
            <Switch 
              checkedChildren="All Sales" 
              unCheckedChildren="Specific Only"
              disabled={!editingDocument && activeTab === 'all-agents'}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={uploadDocumentMutation.isLoading || updateDocumentMutation.isLoading}>
                {editingDocument ? 'Update' : 'Upload'}
              </Button>
              <Button onClick={() => {
                setDocumentModalVisible(false);
                setEditingDocument(null);
                documentForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Permissions Modal */}
      <Modal
        title={`Manage Permissions - ${permissionsType === 'folder' ? 'Folder' : 'Document'}`}
        open={permissionsModalVisible}
        onCancel={() => {
          setPermissionsModalVisible(false);
          setPermissionsType(null);
          setPermissionsId(null);
          permissionsForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={permissionsForm}
          layout="vertical"
          onFinish={handlePermissionsSubmit}
          initialValues={{
            sales_agent_ids: [],
            can_upload: true,
            can_download: true,
            can_delete: false
          }}
        >
          <Form.Item
            name="sales_agent_ids"
            label={isSharedWithAll ? "Sales Agents" : "Sales Agent"}
            rules={[{ required: !isSharedWithAll, message: 'A sales agent must be selected' }]}
          >
            {isSharedWithAll ? (
              <Space>
                <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  All Sales Agents ({agents.length})
                </Tag>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  This item is shared with all {agents.length} sales agent{agents.length !== 1 ? 's' : ''}
                </Text>
              </Space>
            ) : selectedAgentId ? (
              <Select
                mode={undefined}
                disabled
                value={selectedAgentId}
              >
                {agents.map(agent => (
                  <Option key={agent.id} value={agent.id}>
                    {agent.first_name} {agent.last_name} ({agent.email})
                  </Option>
                ))}
              </Select>
            ) : (
              <Select
                mode="multiple"
                placeholder="Select sales agents"
                showSearch
                optionFilterProp="children"
              >
                {agents.map(agent => (
                  <Option key={agent.id} value={agent.id}>
                    {agent.first_name} {agent.last_name} ({agent.email})
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          {permissionsType === 'folder' && (
            <Form.Item
              name="can_upload"
              valuePropName="checked"
            >
              <Switch checkedChildren="Can upload" unCheckedChildren="Cannot upload" />
            </Form.Item>
          )}
          <Form.Item
            name="can_download"
            valuePropName="checked"
          >
            <Switch checkedChildren="Can download" unCheckedChildren="Cannot download" />
          </Form.Item>
          <Form.Item
            name="can_delete"
            valuePropName="checked"
          >
            <Switch checkedChildren="Can delete" unCheckedChildren="Cannot delete" />
          </Form.Item>
          <Alert
            message="Note"
            description={permissionsType === 'folder' ? 'When a folder has access, all documents and subfolders inside it are automatically accessible to the assigned sales agents.' : 'Individual document permissions override folder permissions.'}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={assignPermissionsMutation.isLoading}>
                Save Permissions
              </Button>
              <Button onClick={() => {
                setPermissionsModalVisible(false);
                setPermissionsType(null);
                setPermissionsId(null);
                permissionsForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SalesPortalAdmin;
